import { z } from 'zod';

// ─── Email Format Regex with Domain & TLD ─────────────────────────────────────
export const EMAIL_FORMAT_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

// ─── Individual Field Rules ──────────────────────────────────────────────────
export const emailSchema = z
  .string({ required_error: 'Email address is required' })
  .trim()
  .min(1, 'Email address is required')
  .max(254, 'Email must not exceed 254 characters')
  .regex(EMAIL_FORMAT_REGEX, 'Please enter a valid email address (e.g. name@domain.com)');

export const loginIdentitySchema = z
  .string({ required_error: 'Username or email is required' })
  .trim()
  .min(1, 'Username or email is required');

export const strongPasswordSchema = z
  .string({ required_error: 'Password is required' })
  .min(8, 'Password must be at least 8 characters long')
  .regex(/[A-Z]/, 'Must include at least one uppercase letter (A-Z)')
  .regex(/[a-z]/, 'Must include at least one lowercase letter (a-z)')
  .regex(/[0-9]/, 'Must include at least one number (0-9)');

// ─── Signup Full Schema ──────────────────────────────────────────────────────
export const signupSchema = z
  .object({
    email: emailSchema,
    password: strongPasswordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password')
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword']
  });

// ─── Login Full Schema ───────────────────────────────────────────────────────
export const loginSchema = z.object({
  email: loginIdentitySchema,
  password: z.string().min(1, 'Password is required')
});

// ─── Live Validation Helpers ─────────────────────────────────────────────────
export function validateEmailField(value, isSignUp = false) {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return isSignUp ? 'Email address is required' : 'Username or email is required';
  }
  if (isSignUp) {
    if (!EMAIL_FORMAT_REGEX.test(trimmed)) {
      return 'Please enter a valid email address (e.g. name@domain.com)';
    }
    const result = emailSchema.safeParse(trimmed);
    if (!result.success) {
      const issue = result.error?.issues?.[0] || result.error?.errors?.[0];
      return issue?.message || 'Invalid email address';
    }
    return '';
  }
  if (trimmed.includes('@') && !EMAIL_FORMAT_REGEX.test(trimmed)) {
    return 'Please enter a valid email address';
  }
  const result = loginIdentitySchema.safeParse(trimmed);
  if (!result.success) {
    const issue = result.error?.issues?.[0] || result.error?.errors?.[0];
    return issue?.message || 'Invalid username or email';
  }
  return '';
}

export function validatePasswordField(value, isSignUp = false) {
  if (!isSignUp) {
    return value ? '' : 'Password is required';
  }
  if (!value) return 'Password is required';
  if (value.length < 8) return 'Password must be at least 8 characters long';
  if (!/[A-Z]/.test(value)) return 'Must include at least one uppercase letter (A-Z)';
  if (!/[a-z]/.test(value)) return 'Must include at least one lowercase letter (a-z)';
  if (!/[0-9]/.test(value)) return 'Must include at least one number (0-9)';

  const result = strongPasswordSchema.safeParse(value);
  if (!result.success) {
    const issue = result.error?.issues?.[0] || result.error?.errors?.[0];
    return issue?.message || 'Password does not meet requirements';
  }
  return '';
}

export function validateConfirmPasswordField(password, confirmPassword) {
  if (!confirmPassword) return 'Please confirm your password';
  if (password !== confirmPassword) return 'Passwords do not match';
  return '';
}

export function getPasswordCriteria(password = '') {
  return {
    minLength: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password)
  };
}

export function getPasswordStrength(password = '') {
  if (!password) return { score: 0, label: '', color: '' };
  const criteria = getPasswordCriteria(password);
  const metCount = Object.values(criteria).filter(Boolean).length;

  if (metCount <= 1) return { score: 1, label: 'Weak', color: '#ef4444' };
  if (metCount === 2) return { score: 2, label: 'Fair', color: '#f59e0b' };
  if (metCount === 3) return { score: 3, label: 'Good', color: '#3b82f6' };
  return { score: 4, label: 'Strong', color: '#10b981' };
}
